/**
 * Connects event handlers to buttons that open native HTML <dialog> elements as modal
 * dialogs.
 *
 * The buttons that open dialogs should have a `data-open-modal-dialog` attribute whose
 * value is the `id` of the dialog to open.
 *
 * Example:
 * ```html
 * <button type="button" data-open-modal-dialog="my-dialog">Open Dialog</button>
 * <dialog id="my-dialog">
 *   ...
 * </dialog>
 * ```
 *
 * Dialog trigger buttons can optionally have a `data-modal-dialog-fallback` attribute
 * indicating the ID of a fallback element that is visible by default. If the browser
 * does not support the HTML <dialog> element, then the fallback element will be left
 * visible. This functionality assumes that the button is initially hidden via
 * `style="display: none;"` and the fallback element is visible by default.
 *
 * Example with fallback link:
 * ```html
 * <a id="contact-form-trigger-fallback" href="/contact/">Contact Us</a>
 * <button
 *   type="button"
 *   style="display: none;"
 *   data-open-modal-dialog="contact-form"
 *   data-modal-dialog-fallback="contact-form-trigger-fallback"
 * >
 *   Contact Us
 * </button>
 * <dialog id="contact-form">
 *  ...
 * </dialog>
 * ```
 */
export const configureDialogTriggers = () => {
  document.querySelectorAll("button[data-open-modal-dialog]").forEach((button) => {
    const dialog = document.getElementById(button.dataset?.openModalDialog);
    // If the button has a fallback link and the dialog element is not supported (or is
    // missing), then don't do anything with this button.
    const fallbackLink = document.getElementById(button.dataset?.modalDialogFallback);
    if (fallbackLink && !dialog?.showModal) {
      return;
    }
    // Hide the fallback link and show the button if it's hidden
    button.style.display = "";
    if (fallbackLink) fallbackLink.style.display = "none";
    // Connect the dialog trigger event handler to the button
    button.addEventListener("click", function (event) {
      event.preventDefault();
      event.stopPropagation();
      if (dialog) dialog.showModal();
    });
  });
};

/**
 * Connects event handlers to manage close actions for native HTML <dialog> elements.
 *
 * The following handlers are connected:
 * - Click events on dialog child elements with the `data-dialog-close-button` attribute
 *   to close the dialog when clicked.
 * - Click events outside the dialog to close the dialog when clicking outside of it -
 *   this is done primarily to support Safari that does not yet support the
 *   `closedby="any"` attribute (which would natively handle this functionality
 *   otherwise)
 */
export const configureDialogClose = () => {
  document.querySelectorAll("dialog").forEach((dialog) => {
    // Connect events for the close button if it exists
    dialog
      .querySelectorAll("[data-dialog-close-button]")
      .forEach((button) => button.addEventListener("click", () => dialog.close()));
    // Eventually this can be replaced by the `closedby="any"` attribute on the dialog
    // (currently missing Safari support and brand new in FireFox).
    //
    // We use two event handlers here to ensure that dismissing the modal with a click
    // outside only occurs if the mousedown *and* mouseup both occur outside the dialog.
    // This ensures that closing happens on the mouseup event (which is the usual click
    // behaviour) but also that if the user mouses down outside the dialog, then moves
    // the mouse over the dialog and releases the mouse button, the dialog does not
    // close. Similarly, clicking inside the dialog and releasing outside also does not
    // close the dialog.
    const mouseState = { downOutside: false };
    const eventFromDialogNotChild = (event) => event.target === event.currentTarget;
    dialog.addEventListener(
      "mousedown",
      (event) => (mouseState.downOutside = eventFromDialogNotChild(event)),
    );
    dialog.addEventListener("mouseup", (event) => {
      if (eventFromDialogNotChild(event) && mouseState.downOutside) dialog.close();
      mouseState.downOutside = false;
    });
  });
};
